import { useAssets, useCashflows, useProfile } from '../../hooks/useApi';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { useNavigate } from 'react-router-dom';
import { formatAmount } from '../../utils/numberFormat';
import { DynamicCashFlow, Currency, Country } from '../../types/api.types';
import { OnboardingProgress } from '../../components/OnboardingProgress/OnboardingProgress';
import styles from './DashboardPage.module.scss';

const DEFAULT_LIFE_EXPECTANCY: Record<Country, number> = { INDIA: 80, US: 83, CAN: 84 };

function getRetirementExpenses(cashflows: DynamicCashFlow[]): DynamicCashFlow[] {
    return cashflows.filter((cf) => cf.type === 'EXPENSE' && cf.isRetirementExpense);
}

function convertCurrency(
    amount: number,
    fromCurrency: Currency,
    toCurrency: Currency,
    rates: Record<Currency, number>,
): number {
    if (fromCurrency === toCurrency) return amount;
    return (amount / rates[fromCurrency]) * rates[toCurrency];
}

function getFireMultiplier(retirementYears: number): number {
    if (retirementYears <= 30) return 25;
    if (retirementYears <= 35) return 28;
    if (retirementYears <= 40) return 30;
    if (retirementYears <= 45) return 32;
    return 33;
}

function computeFireMetrics(
    assets: { balance: number; expectedReturn: number }[],
    cashflows: DynamicCashFlow[],
    currentAge: number,
    retirementAge: number,
    baseCurrency: Currency,
    rates: Record<Currency, number>,
    retirementCountry: Country,
) {
    const totalAssets = assets.reduce((sum, a) => sum + a.balance, 0);
    const avgReturn =
        totalAssets > 0 ? assets.reduce((sum, a) => sum + a.expectedReturn * a.balance, 0) / totalAssets / 100 : 0.07;

    const yearsToRetirement = Math.max(0, retirementAge - currentAge);
    const lifeExpectancy = DEFAULT_LIFE_EXPECTANCY[retirementCountry] || 83;
    const retirementYears = lifeExpectancy - retirementAge;
    const fireMultiplier = getFireMultiplier(retirementYears);

    const retirementExpenses = getRetirementExpenses(cashflows);
    const recurringExpenses = retirementExpenses.filter((cf) => !cf.isOneTime);
    const oneTimeExpenses = retirementExpenses.filter((cf) => cf.isOneTime);

    // Retirement expense amounts are entered in retirement-year purchasing power (isRetirementExpense=true),
    // so use cf.amount directly — no pre-retirement inflation adjustment.
    const monthlyRetirementExpense = recurringExpenses.reduce((sum, cf) => {
        const convertedAmount = convertCurrency(cf.amount, cf.currency, baseCurrency, rates);
        return sum + convertedAmount;
    }, 0);
    const annualRetirementExpense = monthlyRetirementExpense * 12;
    const oneTimeTotal = oneTimeExpenses.reduce((sum, cf) => {
        const convertedAmount = convertCurrency(cf.amount, cf.currency, baseCurrency, rates);
        return sum + convertedAmount;
    }, 0);
    const fireNumber = annualRetirementExpense * fireMultiplier + oneTimeTotal;

    const activeIncome = cashflows.filter(
        (cf) => cf.type === 'INCOME' && currentAge >= cf.startAge && currentAge <= cf.endAge,
    );
    const activeExpenses = cashflows.filter(
        (cf) => cf.type === 'EXPENSE' && currentAge >= cf.startAge && currentAge <= cf.endAge,
    );
    const monthlyIncome = activeIncome.reduce(
        (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
        0,
    );
    const monthlyExpenses = activeExpenses.reduce(
        (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
        0,
    );
    const annualSavings = (monthlyIncome - monthlyExpenses) * 12;
    const savingsRate = monthlyIncome > 0 ? ((monthlyIncome - monthlyExpenses) / monthlyIncome) * 100 : 0;

    const progress = fireNumber > 0 ? Math.min((totalAssets / fireNumber) * 100, 100) : 0;

    let yearsRemaining = Infinity;
    if (fireNumber > 0 && annualSavings > 0 && avgReturn > 0) {
        const target = fireNumber;
        const r = avgReturn;
        const s = annualSavings;
        const p = totalAssets;
        if (p >= target) {
            yearsRemaining = 0;
        } else {
            yearsRemaining = Math.log((target * r + s) / (p * r + s)) / Math.log(1 + r);
            yearsRemaining = Math.max(0, Math.ceil(yearsRemaining));
        }
    } else if (fireNumber > 0 && totalAssets >= fireNumber) {
        yearsRemaining = 0;
    }

    let coastFireAge = Infinity;
    if (fireNumber > 0 && totalAssets > 0 && avgReturn > 0) {
        const yearsToCoast = Math.log(fireNumber / totalAssets) / Math.log(1 + avgReturn);
        coastFireAge = Math.ceil(currentAge + yearsToCoast);
    }

    let status: 'ahead' | 'on_track' | 'behind' = 'on_track';
    if (yearsRemaining === 0 || yearsRemaining < yearsToRetirement - 2) {
        status = 'ahead';
    } else if (yearsRemaining > yearsToRetirement) {
        status = 'behind';
    }

    return {
        totalAssets,
        fireNumber,
        fireMultiplier,
        retirementYears,
        lifeExpectancy,
        annualRetirementExpense,
        oneTimeTotal,
        monthlyRetirementExpense,
        progress,
        yearsRemaining,
        savingsRate,
        coastFireAge,
        status,
        yearsToRetirement,
        hasExpenses: retirementExpenses.length > 0,
        hasAssets: assets.length > 0,
    };
}

export function DashboardPage() {
    const { assets, loading: assetsLoading } = useAssets();
    const { cashflows, loading: cashflowsLoading } = useCashflows();
    const { profile, loading: profileLoading } = useProfile();
    const { rates, loading: ratesLoading } = useExchangeRates();
    const navigate = useNavigate();

    const profileRetirementAge = profile?.targetRetirementAge ?? 60;
    const currentAge = profile?.currentAge ?? 35;
    const baseCurrency = profile?.baseCurrency ?? 'USD';
    const retirementCountry: Country = (() => {
        const history = profile?.residencyHistory ?? [];
        const atRetirement = history.find(
            (r) => profileRetirementAge >= r.startAge && profileRetirementAge <= r.endAge,
        );
        if (atRetirement) return atRetirement.country;
        const sorted = [...history].sort((a, b) => b.endAge - a.endAge);
        return sorted[0]?.country ?? 'US';
    })();

    const loading = assetsLoading || cashflowsLoading || profileLoading || ratesLoading;

    if (loading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading your FIRE dashboard...</p>
            </div>
        );
    }

    const metrics = computeFireMetrics(
        assets,
        cashflows,
        currentAge,
        profileRetirementAge,
        baseCurrency,
        rates,
        retirementCountry,
    );

    const assetTotal = assets.reduce((sum, a) => sum + convertCurrency(a.balance, a.currency, baseCurrency, rates), 0);
    const byCountry = assets.reduce<Record<string, number>>((acc, a) => {
        const converted = convertCurrency(a.balance, a.currency, baseCurrency, rates);
        acc[a.country] = (acc[a.country] ?? 0) + converted;
        return acc;
    }, {});

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.title}>FIRE Dashboard</h1>
                <p className={styles.subtitle}>Your path to financial independence</p>
            </div>

            <OnboardingProgress />

            {assets.length > 0 && (
                <div className={styles.assetSummaryCard}>
                    <div className={styles.assetSummaryHeader}>
                        <div>
                            <span className={styles.assetSummaryLabel}>TOTAL ASSETS</span>
                            <span className={styles.assetSummaryAmount}>
                                {formatAmount(Math.round(assetTotal), baseCurrency)}
                            </span>
                            <span className={styles.assetSummaryMeta}>
                                {assets.length} account{assets.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                        <button className={styles.assetSummaryLink} onClick={() => navigate('/assets')}>
                            Manage →
                        </button>
                    </div>
                    {Object.keys(byCountry).length > 1 && (
                        <div className={styles.assetCountryRow}>
                            {Object.entries(byCountry).map(([country, amount]) => (
                                <div key={country} className={styles.assetCountryChip}>
                                    <span className={styles.assetCountryName}>{country}</span>
                                    <span className={styles.assetCountryAmount}>
                                        {formatAmount(Math.round(amount), baseCurrency)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!metrics.hasExpenses ? (
                <div className={styles.emptyPrompt}>
                    <p>Add your retirement budget to calculate your FIRE number.</p>
                    <button className={styles.promptButton} onClick={() => navigate('/cashflows?tab=RETIREMENT')}>
                        + Set Up Retirement Budget
                    </button>
                </div>
            ) : (
                <>
                    <div className={styles.fireCard}>
                        <span className={styles.fireLabel}>YOUR FIRE NUMBER</span>
                        <span className={styles.fireAmount}>
                            {formatAmount(Math.round(metrics.fireNumber), baseCurrency)}
                        </span>
                        <span className={styles.fireBreakdown}>
                            {formatAmount(Math.round(metrics.annualRetirementExpense), baseCurrency)}/yr ×{' '}
                            {metrics.fireMultiplier}
                            {metrics.oneTimeTotal > 0 &&
                                ` + ${formatAmount(Math.round(metrics.oneTimeTotal), baseCurrency)} (one-time)`}
                        </span>
                        <div className={styles.fireExplanation}>
                            Based on {metrics.retirementYears}-year retirement horizon (retire at {profileRetirementAge}
                            , life expectancy {metrics.lifeExpectancy}). Multiplier ×{metrics.fireMultiplier} ={' '}
                            {(100 / metrics.fireMultiplier).toFixed(1)}% safe withdrawal rate.
                        </div>

                        <div className={styles.progressSection}>
                            <div className={styles.progressHeader}>
                                <span className={styles.progressLabel}>
                                    {metrics.progress.toFixed(1)}% of your FIRE goal
                                </span>
                                <span className={styles.progressValues}>
                                    {formatAmount(Math.round(metrics.totalAssets), baseCurrency)} /{' '}
                                    {formatAmount(Math.round(metrics.fireNumber), baseCurrency)}
                                </span>
                            </div>
                            <div className={styles.progressBar}>
                                <div className={styles.progressFill} style={{ width: `${metrics.progress}%` }} />
                            </div>
                        </div>

                        {metrics.monthlyRetirementExpense > 0 && (
                            <div className={styles.inflationNote}>
                                Retirement budget:{' '}
                                {formatAmount(Math.round(metrics.monthlyRetirementExpense), baseCurrency)}/mo in
                                retirement year 1, growing with inflation each year
                            </div>
                        )}
                    </div>

                    <div className={styles.metricsRow}>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>YEARS REMAINING</span>
                            <span
                                className={`${styles.metricValue} ${metrics.yearsRemaining <= metrics.yearsToRetirement ? styles.good : styles.poor}`}
                            >
                                {metrics.yearsRemaining === Infinity ? '—' : metrics.yearsRemaining}
                            </span>
                            <span className={styles.metricStatus}>
                                {metrics.yearsRemaining === Infinity
                                    ? 'Increase savings to compute'
                                    : metrics.yearsRemaining === 0
                                      ? 'You can retire now!'
                                      : `Target: ${metrics.yearsToRetirement} years`}
                            </span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>SAVINGS RATE</span>
                            <span
                                className={`${styles.metricValue} ${metrics.savingsRate >= 50 ? styles.good : metrics.savingsRate >= 30 ? styles.moderate : styles.poor}`}
                            >
                                {metrics.savingsRate.toFixed(0)}%
                            </span>
                            <span className={styles.metricStatus}>
                                {metrics.savingsRate >= 50
                                    ? 'Excellent'
                                    : metrics.savingsRate >= 30
                                      ? 'Good'
                                      : 'Needs improvement'}
                            </span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>COAST FIRE AGE</span>
                            <span
                                className={`${styles.metricValue} ${metrics.coastFireAge <= profileRetirementAge ? styles.good : styles.moderate}`}
                            >
                                {metrics.coastFireAge === Infinity ? '—' : metrics.coastFireAge}
                            </span>
                            <span className={styles.metricStatus}>
                                {metrics.coastFireAge === Infinity
                                    ? 'Add assets to compute'
                                    : metrics.coastFireAge <= currentAge
                                      ? 'Already coasting!'
                                      : 'Stop saving, still retire on time'}
                            </span>
                        </div>
                    </div>

                    <div className={styles.statusRow}>
                        <span className={`${styles.statusBadge} ${styles[metrics.status]}`}>
                            {metrics.status === 'ahead'
                                ? 'Ahead of Schedule'
                                : metrics.status === 'on_track'
                                  ? 'On Track'
                                  : 'Behind Schedule'}
                        </span>
                    </div>
                </>
            )}

            <div className={styles.quickLinks}>
                <button className={styles.linkButton} onClick={() => navigate('/assets')}>
                    {metrics.hasAssets ? 'View Assets →' : 'Add Assets →'}
                </button>
                <button className={styles.linkButton} onClick={() => navigate('/cashflows?tab=RETIREMENT')}>
                    Edit Retirement Budget →
                </button>
                <button className={styles.linkButton} onClick={() => navigate('/simulator')}>
                    Run Simulation →
                </button>
            </div>
        </div>
    );
}
